import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, ArrowRight, Award, Leaf } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { ImageBlock } from "../components/ImageBlock";
import { IMAGES } from "../data/images";
import { Seo } from "../components/Seo";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] } }),
};

export default function Home() {
  const { t, lang } = useLang();
  const points = t("sellingPoints");

  return (
    <div data-testid="home-page">
      <Seo
        title="Sakurų namai — nauji A++ butai Marijampolėje"
        description="Du modernūs A++ energinės klasės namai prie Pašešupio parko Marijampolėje. 1–4 kambarių nauji butai su terasomis ir lodžijomis."
        path="/"
      />
      {/* HERO */}
      <section data-testid="home-hero-section" className="relative h-screen min-h-[640px] flex items-center">
        <div className="absolute inset-0">
          <ImageBlock src={IMAGES.heroNight} alt="Sakurų namai — nauji A++ butai Marijampolėje, Parko g. 1D" className="w-full h-full slow-zoom" testid="hero-image" />
          <div className="absolute inset-0 bg-charcoal/55" />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal via-charcoal/20 to-charcoal/40" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
          <motion.div initial="hidden" animate="show" className="max-w-3xl">
            <motion.p variants={fadeUp} custom={0} className="text-xs uppercase tracking-[0.35em] text-gold mb-6">
              Parko g. 1D · A++
            </motion.p>
            <motion.h1 variants={fadeUp} custom={1} className="brand-logo text-5xl sm:text-6xl lg:text-7xl text-cream leading-[1.05]">
              Sakurų namai
            </motion.h1>
            <motion.p variants={fadeUp} custom={2} className="mt-6 text-lg sm:text-xl text-cream/80 font-light max-w-xl leading-relaxed">
              {t("home.tagline")}
            </motion.p>
            <motion.div variants={fadeUp} custom={3} className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link
                to="/migdolas"
                data-testid="hero-cta-migdolas"
                className="group inline-flex items-center justify-center gap-2 border border-gold/50 text-gold text-xs uppercase tracking-widest px-7 py-4 hover:bg-gold/10 transition-colors"
              >
                {t("home.ctaMigdolas")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/vysnia"
                data-testid="hero-cta-vysnia"
                className="group inline-flex items-center justify-center gap-2 border border-gold/50 text-gold text-xs uppercase tracking-widest px-7 py-4 hover:bg-gold/10 transition-colors"
              >
                {t("home.ctaVysnia")} <ArrowRight size={15} className="group-hover:translate-x-1 transition-transform" />
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* INTRO */}
      <section className="bg-cream text-charcoal py-24">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <p className="text-xs uppercase tracking-[0.3em] text-gold-muted mb-6">{t("home.exploreProject")}</p>
          <p className="font-serif text-2xl sm:text-3xl font-light leading-relaxed text-charcoal">
            {t("home.intro")}
          </p>
          <div className="gold-divider w-32 mx-auto mt-10" />
          <div className="mt-8">
            <Link to="/vieta" data-testid="home-location-link" className="inline-flex items-center gap-2 text-xs uppercase tracking-widest text-gold-muted hover:gap-3 transition-all">
              {t("location.surroundingsTitle")} <ArrowRight size={14} />
            </Link>
          </div>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-8">
            <div className="flex items-center gap-3">
              <Award className="text-gold-muted" size={22} />
              <span className="text-sm text-charcoal/70">{t("about.architectName")}</span>
            </div>
            <div className="flex items-center gap-3">
              <Leaf className="text-gold-muted" size={22} />
              <span className="text-sm text-charcoal/70">{t("common.energyClass")} A++</span>
            </div>
          </div>
        </div>
      </section>

      {/* SELLING POINTS */}
      <section className="bg-charcoal py-24" data-testid="selling-points-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <h2 className="font-serif text-3xl sm:text-4xl font-light text-cream mb-14 text-center">
            {t("home.sellingPointsTitle")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8">
            {Array.isArray(points) &&
              points.map((p, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                  className="flex items-start gap-4 border-b border-cream/10 pb-6"
                  data-testid={`selling-point-${i}`}
                >
                  <span className="mt-1 flex-shrink-0 w-6 h-6 rounded-full border border-gold/40 flex items-center justify-center">
                    <Check size={13} className="text-gold" />
                  </span>
                  <p className="text-cream/80 font-light leading-relaxed">{p}</p>
                </motion.div>
              ))}
          </div>
        </div>
      </section>

      {/* BUILDING SELECT */}
      <section className="bg-charcoal-light py-24">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8">
          {[
            { to: "/migdolas", key: "migdolas", img: IMAGES.migdolasHero, title: "Migdolas", sub: t("about.migdolasDesc") },
            { to: "/vysnia", key: "vysnia", img: IMAGES.vysniaHero, title: "Vyšnia", sub: t("about.vysniaDesc") },
          ].map((b) => (
            <Link key={b.key} to={b.to} data-testid={`building-card-${b.key}`} className="group relative overflow-hidden rounded-lg">
              <ImageBlock src={b.img} alt={b.title} className="h-80 group-hover:scale-105 transition-transform duration-700" overlay />
              <div className="absolute bottom-0 left-0 p-8">
                <h3 className="font-serif text-4xl text-cream">{b.title}</h3>
                <p className="text-sm text-cream/70 mt-2">{b.sub}</p>
                <span className="inline-flex items-center gap-2 mt-4 text-xs uppercase tracking-widest text-gold">
                  {t("common.viewApartments")} <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FINANCE */}
    </div>
  );
}
